import React from 'react';
import { Link } from 'react-router-dom';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';

const LandingPage = () => {
  return (
    <div className="landing-page">
      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-content">
          <h1 className="hero-title">
            PRESERVE YOUR<br />
            MOST VALUABLE<br />
            RELATIONSHIPS<br />
            AT SCALE
          </h1>
          <p className="hero-subtitle">
            AI-powered relationship intelligence platform for professionals managing high-value relationships at scale.
          </p>
          <div className="scroll-indicator">
            <div className="arrow-down">↓</div>
          </div>
        </div>
      </section>

      {/* Section 2: The Paradox */}
      <section className="section-paradox">
        <div className="paradox-container">
          <div className="paradox-left">
            <h2 className="section-display">THE PARADOX<br />OF SUCCESS</h2>
            <p className="body-text">
              Your network is your net worth. Each relationship in your portfolio represents years of trust-building, shared experiences, and mutual value creation.
            </p>
            <p className="body-text">
              Yet the very success that expanded your network makes it impossible to maintain the personal touch that built it.
            </p>
            <div className="statistics-list">
              <div className="stat-item">
                <div className="stat-number">300+</div>
                <div className="stat-description">UHNW relationships across continents and time zones</div>
              </div>
              <div className="stat-divider"></div>
              <div className="stat-item">
                <div className="stat-label">QUARTERLY DINNERS</div>
                <div className="stat-description">Annual galas, and Davos conversations that surface crucial personal details</div>
              </div>
              <div className="stat-divider"></div>
              <div className="stat-item">
                <div className="stat-label">LIFE EVENTS</div>
                <div className="stat-description">Transitions that create pivotal engagement moments</div>
              </div>
              <div className="stat-divider"></div>
              <div className="stat-item">
                <div className="stat-label">COMPETITIVE PRESSURE</div>
                <div className="stat-description">A single overlooked relationship can mean lost mandates worth millions</div>
              </div>
            </div>
          </div>
          <div className="paradox-right">
            <blockquote className="pull-quote">
              "Traditional solutions fail at this level"
            </blockquote>
            <p className="quote-description">
              Executive assistants lack the context and judgment for nuanced relationship management.
            </p>
            <p className="quote-description">
              CRM systems become data graveyards requiring constant manual updates.
            </p>
            <p className="quote-description">
              Generic outreach destroys the authenticity that makes these relationships valuable.
            </p>
            <p className="quote-description">
              The stakes are too high for mass-market automation tools.
            </p>
          </div>
        </div>
      </section>

      {/* Section 3: AI-Powered Platform */}
      <section className="section-bespoke">
        <div className="bespoke-container">
          <h2 className="section-display-inverted">AI-POWERED RELATIONSHIP<br />INTELLIGENCE PLATFORM</h2>
          <p className="subheadline-inverted">
            Advanced AI technology that transforms your communication history into actionable relationship intelligence and automated outreach campaigns.
          </p>
          <div className="phases-grid">
            <div className="phase-column">
              <div className="phase-header">
                <div className="phase-title">PHASE 1</div>
                <div className="phase-name">DISCOVERY</div>
                <div className="phase-divider"></div>
              </div>
              <div className="phase-content">
                <p>Consultative session to understand your industry and relationship landscape</p>
                <p>Define who your valuable contacts are and how they're categorized</p>
                <p>Identify the signals that indicate high-value relationships</p>
                <p>Determine what relationship data is most valuable to extract</p>
                <p>Configure campaign objectives and communication parameters</p>
              </div>
            </div>
            <div className="phase-column">
              <div className="phase-header">
                <div className="phase-title">PHASE 2</div>
                <div className="phase-name">DATA INGEST & ANALYSIS</div>
                <div className="phase-divider"></div>
              </div>
              <div className="phase-content">
                <p>Connect your communication channels via secure OAuth</p>
                <p>AI extracts and structures relationship data from your communication history</p>
                <p>Machine learning identifies key contacts, relationship strength, and engagement patterns</p>
                <p>Natural language processing analyzes communication style and voice</p>
                <p>Automated relationship scoring and prioritization based on your criteria</p>
              </div>
            </div>
            <div className="phase-column">
              <div className="phase-header">
                <div className="phase-title">PHASE 3</div>
                <div className="phase-name">COMMUNICATIONS CAMPAIGNS</div>
                <div className="phase-divider"></div>
              </div>
              <div className="phase-content">
                <p>AI generates personalized messages in your voice for each contact</p>
                <p>Smart scheduling based on optimal engagement times</p>
                <p>All communications delivered in draft form for approval and sending</p>
                <p>Performance analytics and relationship health dashboards</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section 4: Platform Tiers */}
      <section className="section-investment">
        <h2 className="section-display-centered">PLATFORM<br />TIERS</h2>
        <div className="pricing-grid">
          <div className="pricing-card">
            <h3 className="pricing-title">DATA ANALYSIS AND COMPILATION</h3>
            <div className="pricing-divider"></div>
            <div className="pricing-subtitle">One-time fee (TBD)</div>
            <div className="pricing-features">
              <p>AI-powered analysis of 5–7 years of communication history</p>
              <p>Automated relationship profile generation</p>
              <p>Relationship scoring and prioritization</p>
              <p>Structured data export</p>
              <p>30-day platform access for strategy development</p>
            </div>
            <button className="btn-inquire">INQUIRE FOR AVAILABILITY</button>
            <p className="pricing-note">Ideal for professionals who need intelligence infrastructure</p>
          </div>
          <div className="pricing-card">
            <h3 className="pricing-title">ONGOING MESSAGING CAMPAIGN</h3>
            <div className="pricing-divider"></div>
            <div className="pricing-subtitle">Recurring fee (TBD)</div>
            <div className="pricing-features">
              <p>Everything in Data Analysis and Compilation, plus:</p>
              <p>Continuous AI monitoring and relationship updates</p>
              <p>Automated message generation in your voice</p>
              <p>Campaign automation and scheduling</p>
              <p>Performance dashboards and analytics</p>
              <p>Priority platform support</p>
              <p>API access for CRM integration</p>
            </div>
            <button className="btn-inquire">INQUIRE FOR AVAILABILITY</button>
            <p className="pricing-note">Ideal for professionals managing extensive high-value relationship portfolios</p>
          </div>
        </div>
      </section>

      {/* Section 4.75: Impact Scenarios */}
      <section className="section-impact">
        <div className="impact-container">
          <h2 className="section-display-centered">IMPACT<br />SCENARIOS</h2>
          <div className="impact-grid">
            <div className="impact-card">
              <div className="impact-quote">"Reconnected with a client who purchased a six-figure timepiece collection after three years of dormant relationship."</div>
              <div className="impact-attribution">— Luxury Watch Specialist</div>
              <div className="impact-detail">Managing ultra-high-net-worth collector relationships</div>
            </div>
            <div className="impact-card">
              <div className="impact-quote">"Recovered a major allocation by surfacing a dormant LP relationship via our deep archive scan."</div>
              <div className="impact-attribution">— VC Partner</div>
              <div className="impact-detail">Managing 80+ institutional relationships</div>
            </div>
            <div className="impact-card">
              <div className="impact-quote">"Re-engaged three former clients worth significant AUM within the first quarter."</div>
              <div className="impact-attribution">— Wealth Manager</div>
              <div className="impact-detail">250+ high-value relationship portfolio</div>
            </div>
            <div className="impact-card">
              <div className="impact-quote">"Closed two enterprise deals by reconnecting with decision-makers we hadn't spoken to in 18 months."</div>
              <div className="impact-attribution">— Enterprise Sales Director</div>
              <div className="impact-detail">Managing Fortune 500 accounts</div>
            </div>
          </div>
        </div>
      </section>

      {/* Section 6: Security & Discretion */}
      <section className="section-security">
        <div className="security-container">
          <h2 className="section-display-inverted-centered">TRUST AS<br />INFRASTRUCTURE</h2>
          <p className="body-text-inverted-centered">
            We understand that the relationships in your network represent privileged information.
          </p>
          <p className="body-text-inverted-centered">
            Our security protocols were designed for financial services compliance:
          </p>
          <div className="security-grid">
            <div className="security-item">
              <div className="security-title">TRUSTED BY MAJOR RIAS</div>
              <div className="security-description">We work with some of the largest Registered Investment Advisor networks in the country</div>
            </div>
            <div className="security-item">
              <div className="security-title">MULTIPLE YEARLY AUDITS</div>
              <div className="security-description">Subject to rigorous security, encryption, data management, and access practice audits</div>
            </div>
            <div className="security-item">
              <div className="security-title">ZERO-KNOWLEDGE ARCHITECTURE</div>
              <div className="security-description">Raw communications are processed then permanently deleted</div>
            </div>
            <div className="security-item">
              <div className="security-title">NDAS STANDARD</div>
              <div className="security-description">For all team members with security clearances</div>
            </div>
            <div className="security-item">
              <div className="security-title">SEGREGATED INTELLIGENCE</div>
              <div className="security-description">Your relationship data is never co-mingled with other clients</div>
            </div>
            <div className="security-item">
              <div className="security-title">IMMEDIATE DATA DELETION</div>
              <div className="security-description">Upon service termination, certified and documented</div>
            </div>
          </div>
          <p className="security-footer">References available from family offices, RIAs, and institutional clients (disclosed only with their permission)</p>
        </div>
      </section>

      {/* Section 7: The Team */}
      <section className="section-team">
        <div className="team-container">
          <div className="team-left">
            <h2 className="section-display">WHO<br />WE ARE</h2>
            <p className="body-text">
              We're technologists who understand that relationships drive business.
            </p>
            <p className="body-text">
              We built AI systems to solve a problem every successful professional faces: maintaining authentic connections at scale.
            </p>
          </div>
          <div className="team-right">
            <blockquote className="team-quote">
              "We have spent years at the highest levels of relationship management and watched brilliant professionals lose tens of millions in business simply because they couldn't scale genuine attention. This is the system we wish we'd had."
            </blockquote>
            <div className="team-founders">
              <div className="founder-bio">
                <p className="founder-link"><a href="https://www.linkedin.com/in/meet-clinton/" target="_blank" rel="noopener noreferrer">Clinton Agresti</a>, CEO</p>
                <p className="founder-description">Built relationship intelligence systems for managing 500+ UHNW portfolios. Background in private banking and wealth management technology.</p>
              </div>
              <div className="founder-bio">
                <p className="founder-link"><a href="https://www.linkedin.com/in/imrobcarrington/" target="_blank" rel="noopener noreferrer">Rob Carrington</a>, CTO</p>
                <p className="founder-description">AI/ML engineer specializing in natural language processing and secure data systems. Previously built infrastructure for financial services technology.</p>
              </div>
              <div className="founder-bio">
                <p className="founder-link"><a href="https://www.linkedin.com/in/fernandopizarro" target="_blank" rel="noopener noreferrer">Fernando Pizarro</a>, COO</p>
                <p className="founder-description">Product and operations leader focused on building scalable platforms. Background in SaaS and enterprise software delivery.</p>
              </div>
            </div>
            <div className="team-managers">
              <p className="team-note">
                Our platform serves a limited number of clients to ensure platform performance and data security standards.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Section 9: FAQ */}
      <section className="section-faq">
        <h2 className="section-display-centered">FREQUENTLY<br />ASKED QUESTIONS</h2>
        <div className="faq-container">
          <Accordion type="single" collapsible className="faq-accordion">
            <AccordionItem value="item-1">
              <AccordionTrigger className="faq-question">
                HOW IS THIS DIFFERENT FROM A CRM?
              </AccordionTrigger>
              <AccordionContent className="faq-answer">
                <p>Traditional CRMs are databases that require constant manual updates. DashFox One is an AI-powered intelligence platform that:</p>
                <p>(1) Automatically extracts relationship data from your existing communications</p>
                <p>(2) Generates personalized messages in your voice using natural language AI</p>
                <p>(3) Continuously monitors for trigger events and engagement opportunities</p>
                <p>Think of it as relationship intelligence automation, not just data storage.</p>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-2">
              <AccordionTrigger className="faq-question">
                WHAT AI TECHNOLOGY POWERS THE PLATFORM?
              </AccordionTrigger>
              <AccordionContent className="faq-answer">
                <p>We use advanced natural language processing models trained on professional communication patterns.</p>
                <p>Our AI analyzes your historical communications to understand your voice, then generates personalized messages that match your style.</p>
                <p>All messages are queued for your review before sending—you maintain full control.</p>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-3">
              <AccordionTrigger className="faq-question">
                HOW SECURE IS MY DATA?
              </AccordionTrigger>
              <AccordionContent className="faq-answer">
                <p>We use bank-level security: AES-256 encryption, OAuth authentication (we never see your passwords), and SOC 2 compliance.</p>
                <p>Raw email content is processed then deleted—we only retain structured relationship intelligence.</p>
                <p>You can revoke access at any time through your email provider's security settings.</p>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-4">
              <AccordionTrigger className="faq-question">
                WHAT ABOUT COMPLIANCE FOR REGULATED PROFESSIONALS?
              </AccordionTrigger>
              <AccordionContent className="faq-answer">
                <p>We have extensive experience with SEC, FINRA, and other regulatory frameworks.</p>
                <p>Our platform can operate under your firm's compliance oversight and provide audit trails as needed.</p>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </section>

      {/* Section 10: Contact */}
      <section className="section-contact">
        <div className="contact-container">
          <h2 className="contact-title">REQUEST<br />ACCESS</h2>
          <p className="contact-subtitle">
            Due to the intensive nature of our work, we maintain a limited client roster. 
            To begin the conversation, reach out directly.
          </p>
          <div className="contact-details">
            <a href="mailto:hello@dashfox.one" className="contact-link">HELLO@DASHFOX.ONE</a>
            <a href="tel:+14154032108" className="contact-link">+1 (415) 403-2108</a>
          </div>
          <p className="contact-note">
            All inquiries are handled personally and confidentially.<br />
            We typically respond within 2 business days.
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="footer-container">
          <div className="footer-column">
            <div className="footer-title">TRUST INDICATORS</div>
            <p className="footer-text">Trusted by major RIA networks</p>
            <p className="footer-text">Multiple yearly security audits</p>
            <p className="footer-text">Client data encrypted with AES-256</p>
          </div>
          <div className="footer-column">
            <div className="footer-brand">DASHFOX ONE</div>
            <p className="footer-text">© 2025 DashFox One</p>
            <p className="footer-text">All rights reserved</p>
          </div>
          <div className="footer-column">
            <div className="footer-title">LEGAL</div>
            <Link to="/privacy-policy" className="footer-link">Privacy Policy</Link>
            <Link to="/terms-of-service" className="footer-link">Terms of Service</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;